import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Globe, TrendingUp, Landmark, Lock, Menu } from 'lucide-react';

interface MobileTabBarProps {
  onMenuOpen: () => void;
  bankerLocked?: boolean;
}

const MobileTabBar: React.FC<MobileTabBarProps> = ({ onMenuOpen, bankerLocked = false }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (tab: string): boolean => {
    switch (tab) {
      case 'home':
        return location.pathname === '/';
      case 'markets':
        return location.pathname.startsWith('/markets') || location.pathname.startsWith('/screener');
      case 'banker':
        return location.pathname.startsWith('/trading/banker');
      case 'trading':
        return location.pathname.startsWith('/trading') && !location.pathname.startsWith('/trading/banker');
      default:
        return false;
    }
  };

  const tabs = [
    {
      key: 'home',
      label: 'Home',
      icon: Home,
      onClick: () => navigate('/'),
    },
    {
      key: 'markets',
      label: 'Markets',
      icon: Globe,
      onClick: () => navigate('/markets'),
    },
    {
      key: 'trading',
      label: 'Trading',
      icon: TrendingUp,
      onClick: () => navigate('/trading'),
    },
    {
      key: 'banker',
      label: 'Banker',
      icon: Landmark,
      onClick: () => navigate('/trading/banker/leads'),
    },
    {
      key: 'menu',
      label: 'Menu',
      icon: Menu,
      onClick: onMenuOpen,
    },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-slate-800 border-t border-slate-700/50">
      <div className="flex items-stretch">
        {tabs.map(({ key, label, icon: Icon, onClick }) => {
          const active = key !== 'menu' && isActive(key);
          return (
            <button
              type="button"
              key={key}
              onClick={onClick}
              className={`flex flex-col items-center justify-center flex-1 py-2 px-1 min-h-[44px] min-w-[44px] transition-colors
                ${active ? 'text-[var(--accent-400)]' : 'text-slate-400 hover:text-slate-200'}`}
              aria-label={label}
              aria-current={active ? 'page' : undefined}
            >
              <span className="relative">
                <Icon size={22} />
                {key === 'banker' && bankerLocked && (
                  <Lock size={11} className="absolute -right-2 -top-1 text-amber-300" aria-label="Subscription required" />
                )}
              </span>
              <span className="text-xs mt-1">{label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileTabBar;
